import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';

export default function TermsOfUse({ isOpen, onClose }) {
  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-8 px-4"
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 40 }}
          transition={{ duration: 0.3 }}
          className="relative w-full max-w-3xl bg-dark-900 border border-white/10 rounded-2xl p-8 md:p-12"
        >
          <button
            onClick={onClose}
            className="absolute top-6 right-6 p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            <X size={20} className="text-gray-400" />
          </button>

          <h1 className="text-3xl font-bold text-white mb-2">Terms of Use</h1>
          <p className="text-sm text-gray-400 mb-8">Last updated: June 16, 2026</p>

          <div className="space-y-8 text-gray-300 text-sm leading-relaxed">

            <section>
              <h2 className="text-lg font-semibold text-white mb-3">1. Acceptance of Terms</h2>
              <p>
                By accessing or using the Primeslink website (<span className="text-primary-400">primeslink.com</span>) and related services (collectively, the "Services"), you agree to be bound by these Terms of Use ("Terms"). If you do not agree to these Terms, please do not access or use our Services. These Terms apply to all visitors, users, and others who access the Services.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-white mb-3">2. Services Description</h2>
              <p>
                Primeslink provides AI automation consulting and implementation services for businesses. Our Services include, but are not limited to, AI workflow automation, chatbot deployment, process automation consulting, and related technology solutions. We also operate an AI-powered chat assistant on our website to answer inquiries and collect lead information.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-white mb-3">3. AI Disclosure</h2>
              <p className="mb-3">
                Our website features an AI-powered chat assistant. By interacting with this assistant, you acknowledge:
              </p>
              <ul className="list-disc list-inside space-y-2 pl-2">
                <li>The chat assistant is powered by artificial intelligence, not a human agent.</li>
                <li>AI responses are generated algorithmically and may occasionally be inaccurate, incomplete, or not applicable to your specific situation.</li>
                <li>Information provided through the AI chat does not constitute professional, legal, financial, or business advice.</li>
                <li>You should independently verify any information provided before making business decisions.</li>
                <li>Conversations may be reviewed by Primeslink staff for quality assurance and service improvement purposes.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-white mb-3">4. User Conduct</h2>
              <p className="mb-3">When using our Services, you agree not to:</p>
              <ul className="list-disc list-inside space-y-2 pl-2">
                <li>Submit false, misleading, or fraudulent information</li>
                <li>Use the Services for any unlawful purpose or in violation of applicable regulations</li>
                <li>Attempt to interfere with, compromise, or disrupt our website or servers</li>
                <li>Use automated tools to scrape, harvest, or collect data from our Services</li>
                <li>Impersonate any person or entity, or misrepresent your affiliation with any person or entity</li>
                <li>Transmit any spam, unsolicited communications, or malicious content through our Services</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-white mb-3">5. Consultations & Engagements</h2>
              <p className="mb-3">
                Booking a free strategy call or consultation through our Services does not create a binding contractual obligation on either party. Formal service agreements, scope of work, and payment terms will be established in separate written contracts prior to commencement of any paid work.
              </p>
              <p>
                We reserve the right to decline any engagement or consultation request at our sole discretion.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-white mb-3">6. Intellectual Property</h2>
              <p>
                All content on the Primeslink website — including text, graphics, logos, images, and software — is the property of Primeslink and is protected by applicable intellectual property laws. You may not reproduce, distribute, modify, or create derivative works from any content on this site without our prior written consent.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-white mb-3">7. Third-Party Links & Services</h2>
              <p>
                Our Services may contain links to or integrations with third-party websites and services (e.g., Calendly for scheduling). These are provided for convenience only. Primeslink has no control over, and assumes no responsibility for, the content, privacy policies, or practices of any third-party services. We encourage you to review the terms and privacy policies of any third-party services you access.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-white mb-3">8. Disclaimer of Warranties</h2>
              <p>
                The Services are provided on an "as is" and "as available" basis without warranties of any kind, either express or implied, including but not limited to warranties of merchantability, fitness for a particular purpose, or non-infringement. Primeslink does not warrant that the Services will be uninterrupted, error-free, or free of viruses or other harmful components.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-white mb-3">9. Limitation of Liability</h2>
              <p>
                To the fullest extent permitted by applicable law, Primeslink and its officers, directors, employees, and agents shall not be liable for any indirect, incidental, special, consequential, or punitive damages — including loss of profits, data, or business opportunities — arising from your use of, or inability to use, our Services. Our total liability for any claim arising under these Terms shall not exceed the amount paid by you to Primeslink in the twelve (12) months preceding the claim, or $100 USD, whichever is greater.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-white mb-3">10. Privacy</h2>
              <p>
                Your use of our Services is also governed by our{' '}
                <span className="text-primary-400">Privacy Policy</span>, which is incorporated into these Terms by reference. By using our Services, you consent to the collection and use of your information as described in our Privacy Policy.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-white mb-3">11. Changes to Terms</h2>
              <p>
                We reserve the right to modify these Terms at any time. We will notify users of material changes by updating the "Last updated" date at the top of this page. Your continued use of our Services after any changes constitutes your acceptance of the revised Terms. We encourage you to review these Terms periodically.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-white mb-3">12. Governing Law</h2>
              <p>
                These Terms shall be governed by and construed in accordance with the laws of the State of New York, United States, without regard to its conflict of law provisions. Any disputes arising under these Terms shall be subject to the exclusive jurisdiction of the courts located in New York.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-white mb-3">13. Contact Us</h2>
              <p>If you have questions about these Terms, please contact us:</p>
              <div className="mt-3 p-4 bg-white/5 rounded-lg border border-white/10 space-y-1">
                <p className="text-white font-medium">Primeslink</p>
                <p>Email: <a href="mailto:info@primeslink.com" className="text-primary-400 hover:underline">info@primeslink.com</a></p>
                <p>Phone: <a href="tel:+19145255267" className="text-primary-400 hover:underline">+1 914 525 5267</a></p>
              </div>
            </section>
          </div>

          <div className="mt-10 pt-6 border-t border-white/10 text-center">
            <button
              onClick={onClose}
              className="px-8 py-3 bg-gradient-to-r from-primary-500 to-accent-500 text-white rounded-lg font-semibold hover:shadow-lg transition-shadow cursor-pointer"
            >
              Close
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
